// Header bileşeni - Üst menü ve kullanıcı bilgileri
import React, { useState } from 'react';
import ConfirmModal from '../Common/ConfirmModal';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { 
  FiMenu, 
  FiUser, 
  FiSettings, 
  FiLogOut,
  FiSun,
  FiMoon,
  FiX
} from 'react-icons/fi';
import { useForm } from 'react-hook-form';
import { useMutation } from 'react-query';
import { userService } from '../../services/userService';
import toast from 'react-hot-toast';

const Header = ({ onMenuClick }) => {
  const { user, logout } = useAuth();
  const { isDarkMode, toggleTheme } = useTheme();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showLogoutAfterEdit, setShowLogoutAfterEdit] = useState(false);
  const [logoutCountdown, setLogoutCountdown] = useState(0);

  // Form yönetimi
  const { register, handleSubmit, formState: { errors }, setValue, watch, setError, clearErrors } = useForm();

  // Profil güncelleme mutation
  const updateProfileMutation = useMutation(
    ({ id, ...data }) => userService.updateUser(id, data),
    {
      onSuccess: () => {
        // Toast mesajını sadece bir kere göster
        toast.success('Profil başarıyla güncellendi');
        setShowProfileModal(false);
        setShowLogoutAfterEdit(true);
        setLogoutCountdown(5); // 5 saniye geri sayım başlat
      },
      onError: (error) => {
        toast.error('Profil güncelleme hatası: ' + (error.response?.data?.message || error.message));
      }
    }
  );

  // Profil düzenleme fonksiyonu
  const handleEditProfile = () => {
    // Debug: Kullanıcı bilgilerini kontrol et
    
    // Form değerlerini sıfırla ve kullanıcı bilgilerini doldur
    setValue('user_fullname', user?.user_fullname || '');
    setValue('user_email', user?.user_email || '');
    setValue('user_phone', user?.user_phone || user?.user_gsm || '');
    setValue('password', '');
    setValue('confirmPassword', '');
    setShowProfileModal(true);
    setShowUserMenu(false);
  };

  // Profil modal kapatma fonksiyonu
  const handleCloseProfileModal = () => {
    setShowProfileModal(false);
    // Form state'ini tamamen temizle
    setValue('user_fullname', '');
    setValue('user_email', '');
    setValue('user_phone', '');
    setValue('password', '');
    setValue('confirmPassword', '');
    // Form hatalarını temizle
    clearErrors();
  };

  // Profil kaydetme fonksiyonu
  const onSubmitProfile = (data) => {
    // user_phone'u user_gsm olarak gönder ve eksik alanları ekle
    const profileData = {
      ...data,
      user_gsm: data.user_phone, // user_phone'u user_gsm olarak gönder
      user_department: user?.user_department || '', // Mevcut departman
      user_type: user?.user_type || 'agent', // Mevcut kullanıcı tipi
      user_manager: user?.user_manager || null, // Mevcut yönetici
      user_status: user?.user_status || 1, // Mevcut durum (1 = aktif)
      user_phone: undefined // user_phone'u kaldır
    };
    delete profileData.user_phone;
    
    // Debug: user ID'yi kontrol et
    
    // Backend user_uuid bekliyor, ama bizde yok. user.id kullanacağız
    // Önce user_uuid'yi backend'den alalım
    updateProfileMutation.mutate({ id: user?.id, ...profileData });
  };

  // Logout after edit onayı
  const handleLogoutAfterEdit = () => {
    setShowLogoutAfterEdit(false);
    setLogoutCountdown(0);
    logout();
  };

  // Geri sayım useEffect'i
  React.useEffect(() => {
    let interval;
    if (showLogoutAfterEdit && logoutCountdown > 0) {
      interval = setInterval(() => {
        setLogoutCountdown(prev => {
          if (prev <= 1) {
            // Geri sayım bitti, otomatik logout
            setShowLogoutAfterEdit(false);
            logout();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [showLogoutAfterEdit, logoutCountdown, logout]);

  // Çıkış yapma fonksiyonu (onay modalı ile)
  const handleLogout = () => {
    setShowLogoutConfirm(true);
  };

  const confirmLogout = async () => {
    await logout();
    setShowLogoutConfirm(false);
    setShowUserMenu(false);
  };

  const cancelLogout = () => {
    setShowLogoutConfirm(false);
  };

  return (
    <header className="header">
      <div className="flex items-center justify-between">
        {/* Sol taraf - Logo ve Menü butonu */}
        <div className="flex items-center space-x-4">
          {/* Logo */}
          <div className="flex items-center space-x-3">
            <img 
              src="/apro_logo.png" 
              alt="APRO Logo" 
              className="h-10 w-auto md:h-12"
              style={{ aspectRatio: '200/135' }}
            />
          </div>
          
          {/* Mobile menü butonu */}
          <button
            onClick={onMenuClick}
            className="lg:hidden text-white hover:text-gray-300 p-2"
          >
            <FiMenu size={24} />
          </button>
        </div>

        {/* Sağ taraf - Tema ve kullanıcı menüsü */}
        <div className="flex items-center space-x-4">
          {/* Tema değiştirme butonu */}
          <button
            onClick={toggleTheme}
            className="text-white hover:text-gray-300 p-2"
            title={isDarkMode ? 'Açık tema' : 'Koyu tema'}
          >
            {isDarkMode ? <FiSun size={20} /> : <FiMoon size={20} />}
          </button>

          {/* Kullanıcı menüsü */}
          <div className="relative">
            <button
              onClick={() => setShowUserMenu(!showUserMenu)}
              className="flex items-center space-x-3 text-white hover:text-gray-300 p-2"
            >
              <div className="h-8 w-8 bg-brand-secondary rounded-full flex items-center justify-center">
                <span className="text-white font-medium text-sm">
                  {user?.user_fullname?.charAt(0)?.toUpperCase()}
                </span>
              </div>
              <div className="hidden md:block text-left">
                <p className="text-sm font-medium">{user?.user_fullname}</p>
                <p className="text-xs text-gray-300">
                  {user?.user_type === 'admin' ? 'Admin' : 
                   user?.user_type === 'manager' ? 'Manager' : 'User'}
                </p>
              </div>
            </button>

            {/* Kullanıcı dropdown menüsü */}
            {showUserMenu && (
              <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-50">
                <div className="py-1">
                  {/* Kullanıcı bilgileri */}
                  <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      {user?.user_fullname}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Role: {user?.user_type === 'admin' ? 'Admin' : 
                             user?.user_type === 'manager' ? 'Manager' : 'User'}
                    </p>
                  </div>

                  {/* Menü öğeleri */}
                  <button
                    onClick={handleEditProfile}
                    className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    <FiUser className="mr-3" size={16} />
                    Profil
                  </button>
                  
                  <a
                    href="/settings"
                    className="flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                    onClick={() => setShowUserMenu(false)}
                  >
                    <FiSettings className="mr-3" size={16} />
                    Ayarlar
                  </a>

                  <hr className="my-1 border-gray-200 dark:border-gray-700" />
                  
                  <button
                    onClick={handleLogout}
                    className="flex items-center w-full px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    <FiLogOut className="mr-3" size={16} />
                    Çıkış Yap
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Dropdown menü dışına tıklandığında kapat */}
      {showUserMenu && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setShowUserMenu(false)}
        />
      )}

      {showLogoutConfirm && (
        <ConfirmModal
          title="Çıkış Onayı"
          message="Çıkış yapmak istediğinize emin misiniz?"
          onConfirm={confirmLogout}
          onCancel={cancelLogout}
          confirmText="Çıkış Yap"
          cancelText="İptal"
        />
      )}

      {/* Profil Düzenleme Modalı */}
      {showProfileModal && (
        <div className="modal-overlay">
          <div className="modal-content max-w-md">
            {/* Modal header */}
            <div className="modal-header">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-white">
                  Profil Düzenle
                </h3>
                <button
                  onClick={handleCloseProfileModal}
                  className="text-white hover:text-gray-300"
                >
                  <FiX size={20} />
                </button>
              </div>
            </div>
            
            {/* Modal body */}
            <form onSubmit={handleSubmit(onSubmitProfile)} className="p-6 space-y-6">
              <div className="space-y-4">
                {/* Ad Soyad */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Ad Soyad *
                  </label>
                  <input
                    type="text"
                    {...register('user_fullname', { required: 'Ad soyad gerekli' })}
                    className="input-field text-gray-900 dark:text-white"
                    placeholder="Ad soyad girin"
                  />
                  {errors.user_fullname && (
                    <p className="mt-1 text-sm text-red-600">{errors.user_fullname.message}</p>
                  )}
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    {...register('user_email', { 
                      required: 'Email gerekli',
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: 'Geçerli bir email adresi girin'
                      }
                    })}
                    className="input-field text-gray-900 dark:text-white"
                    placeholder="Email adresi girin"
                  />
                  {errors.user_email && (
                    <p className="mt-1 text-sm text-red-600">{errors.user_email.message}</p>
                  )}
                </div>

                {/* Telefon */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Telefon *
                  </label>
                  <input
                    type="tel"
                    {...register('user_phone', { required: 'Telefon numarası gerekli' })}
                    className="input-field text-gray-900 dark:text-white"
                    placeholder="Telefon numarası"
                  />
                  {errors.user_phone && (
                    <p className="mt-1 text-sm text-red-600">{errors.user_phone.message}</p>
                  )}
                </div>

                {/* Şifre Uyarısı */}
                <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md p-3">
                  <p className="text-sm text-yellow-800 dark:text-yellow-200">
                    <strong>Not:</strong> Şifre değiştirmek istemiyorsanız şifre alanlarını boş bırakın.
                  </p>
                </div>

                {/* Şifre */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Yeni Şifre
                  </label>
                  <input
                    type="password"
                    {...register('password')}
                    className="input-field text-gray-900 dark:text-white"
                    placeholder="Yeni şifre (opsiyonel)"
                    autoComplete="new-password"
                    data-form-type="other"
                  />
                </div>

                {/* Şifre Tekrar */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Şifre Tekrar
                  </label>
                  <input
                    type="password"
                    {...register('confirmPassword', {
                      validate: (value) => {
                        const password = watch('password');
                        if (password && value !== password) {
                          return 'Şifreler eşleşmiyor';
                        }
                        return true;
                      }
                    })}
                    className="input-field text-gray-900 dark:text-white"
                    placeholder="Şifre tekrar (opsiyonel)"
                    autoComplete="new-password"
                    data-form-type="other"
                  />
                  {errors.confirmPassword && (
                    <p className="mt-1 text-sm text-red-600">{errors.confirmPassword.message}</p>
                  )}
                </div>
              </div>

              {/* Modal footer */}
              <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-600">
                <button
                  type="button"
                  onClick={handleCloseProfileModal}
                  className="btn-secondary"
                >
                  İptal
                </button>
                <button
                  type="submit"
                  disabled={updateProfileMutation.isLoading}
                  className="btn-primary"
                >
                  {updateProfileMutation.isLoading ? 'Kaydediliyor...' : 'Kaydet'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Logout After Edit Modal */}
      {showLogoutAfterEdit && (
        <div className="modal-overlay">
          <div className="modal-content max-w-md">
            {/* Modal header */}
            <div className="modal-header">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-white">
                  Profil Güncellendi
                </h3>
              </div>
            </div>
            
            {/* Modal body */}
            <div className="p-6">
              <p className="text-gray-700 dark:text-gray-300 mb-6">
                Profiliniz başarıyla güncellendi. Değişikliklerin etkili olması için çıkış yapıp tekrar giriş yapmanız gerekiyor.
              </p>
              
              <div className="flex justify-center">
                <button
                  onClick={handleLogoutAfterEdit}
                  className="btn-primary"
                >
                  {logoutCountdown > 0 ? `Tamam (${logoutCountdown})` : "Tamam"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
